import React, { useState, useEffect, useCallback } from 'react';
import Slider from 'react-slick';
import Navbar from './Navbar';
import './ProductList.css';
import { useAuth } from '../components/AuthContext';
import useCartContext from '../hooks/useCartContext'; 
import usePagination from '../hooks/usePagination'; // 🔹 Hook optimizado
import QuantityModal from './QuantityModal';
import ProductFilters from './ProductFilters';
import ProductItem from './ProductItem';
import BusinessGallery from './BusinessGallery';
import OffersGallery from './OffersGallery';
import WhatsAppButton from './WhatsAppButton';
import Footer from './Footer';

const NextArrow = ({ onClick }) => (
  <div className="slick-arrow slick-next" onClick={onClick}>
    ▶
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div className="slick-arrow slick-prev" onClick={onClick}>
    ◀
  </div>
);

const carouselSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 2000,
  fade: true,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
};

const Notification = ({ message, type, duration, onClose }) => {
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div className={`notification ${type}`}>
      <p>{message}</p>
      <button onClick={onClose} className="notification-close">
        &times;
      </button>
    </div>
  );
};

const ProductList = () => {
  const ITEMS_PER_PAGE = 8;
  
  // 🔹 Hook de paginación optimizado
  const {
    items: displayProducts,
    isLoading,
    hasNextPage,
    hasPrevPage,
    loadFirstPage,
    loadNextPage,
    loadPrevPage,
    currentPage
  } = usePagination(ITEMS_PER_PAGE);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [carouselImages, setCarouselImages] = useState([]);
  const [notification, setNotification] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentUser } = useAuth();
  const { cart, setCart } = useCartContext();

  useEffect(() => {
    const updateCarouselImages = () => {
      if (window.innerWidth <= 768) {
        setCarouselImages([
          { id: 1, src: '/img/mobile_offer1.png', alt: 'Oferta 1 Móvil' },
          { id: 2, src: '/img/mobile_offer2.png', alt: 'Oferta 2 Móvil' },
          { id: 3, src: '/img/mobile_offer3.png', alt: 'Oferta 3 Móvil' },
        ]);
      } else {
        setCarouselImages([
          { id: 1, src: '/img/offer1.png', alt: 'Oferta 1' },
          { id: 2, src: '/img/offer2.png', alt: 'Oferta 2' },
          { id: 3, src: '/img/offer3.png', alt: 'Oferta 3' },
        ]);
      }
    };

    updateCarouselImages();
    window.addEventListener('resize', updateCarouselImages);
    return () => {
      window.removeEventListener('resize', updateCarouselImages);
    };
  }, []);

  // 🔹 Cargar todos los productos AL MONTAR (primera vez)
  useEffect(() => {
    loadFirstPage({
      category: null,
      brands: [],
      searchTerm: '',
      enabled: true
    });
  }, []); // Solo una vez al montar

  // 🔹 Recargar productos SOLO cuando: categoría + marca O búsqueda
  useEffect(() => {
    // Solo recargar si:
    // 1. Hay categoría Y marca seleccionadas
    // 2. O hay búsqueda de texto
    if ((selectedCategory && selectedBrands.length > 0) || searchTerm) {
      loadFirstPage({
        category: selectedCategory,
        brands: selectedBrands,
        searchTerm: searchTerm,
        enabled: true
      });
    }
    // Si solo hay categoría sin marca: NO hacer nada (evita recarga innecesaria)
  }, [selectedCategory, selectedBrands, searchTerm, loadFirstPage]);
  // 🔹 Manejar cambios de filtros (resetea a página 1)
  const handleFilterSelect = useCallback(({ category, brands, searchTerm: newSearchTerm }) => {
    setSelectedCategory(category);
    setSelectedBrands(brands || []);
    setSearchTerm(newSearchTerm || '');
    // El useEffect anterior se dispara y carga la primera página automáticamente
  }, []);
  
  const totalItemsInCart = cart.length;

  const handleAddToCart = (product) => {
    if (!currentUser) {
      setNotification({ message: 'Por favor, inicia sesión para agregar productos al carrito.', type: 'error', duration: 2000 });
      return;
    }
  
    const productId = product.id; // ID de Firestore
    const productName = product.name; // Nombre del producto
    const productPrice = parseFloat(product.price); // Precio original del producto
    const productDiscount = parseFloat(product.discount) || 0; // Descuento en porcentaje, si no hay, es 0
  
    // Cálculo del precio final con descuento aplicado
    const discountedPrice = productDiscount > 0
      ? (productPrice * (1 - productDiscount / 100)).toFixed(2)
      : productPrice;
  
    console.log(`Agregando producto con ID: ${productId}, nombre: ${productName}, precio con descuento: ${discountedPrice}`);
  
    // Crear una nueva versión del producto con el precio descontado
    const productWithDiscount = {
      ...product,
      finalPrice: discountedPrice // Agregamos el precio final con descuento
    };
  
    setSelectedProduct(productWithDiscount); // Ahora agregamos el producto con el precio final
    setIsModalOpen(true); // Abre el modal
  };
  

  const handleConfirmQuantity = (quantity) => {
    const product = selectedProduct;
    const existingProduct = cart.find(item => item.id === product.id);
  
    if (existingProduct) {
      const updatedCart = cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
      );
      setCart(updatedCart);
    } else {
      setCart([...cart, { ...product, quantity }]);
    }
  
    setNotification({ message: 'Producto agregado al carrito.', type: 'success', duration: 2000 });
    setIsModalOpen(false);
  };

  const handleRemoveFromCart = (productId) => {
    const updatedCart = cart.filter(item => item.id !== productId);
    setCart(updatedCart);
    setNotification({ message: 'Producto removido del carrito.', type: 'success', duration: 2000 });
  };

  const isInCart = (productId) => {
    return cart.some(item => item.id === productId);
  };

  return (
    <div className="product-list-wrapper">
      <Navbar cartItemCount={totalItemsInCart} />
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="carousel-container">
        <Slider {...carouselSettings}>
          {carouselImages.map(image => (
            <div key={image.id} className="carousel-slide">
              <img src={image.src} alt={image.alt} className="carousel-image" />
            </div>
          ))}
        </Slider>
      </div>

      <div className="product-content">
        {/* Filtrado de productos en componente separado */}
        <ProductFilters onFilterSelect={handleFilterSelect} />
  
        <div className="product-list">
          {isLoading ? ( 
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Cargando productos...</p>
            </div>
          ) : displayProducts.length > 0 ? (
            <>
              {displayProducts.map(product => (
                <ProductItem
                  key={product.id}
                  product={product}
                  isInCart={isInCart(product.id)}
                  onAddToCart={handleAddToCart}
                  onRemoveFromCart={() => handleRemoveFromCart(product.id)}
                />
              ))}
            </>
          ) : (
            <p>No se encontraron productos.</p>
          )}
       </div>

       {/* Paginación optimizada - Solo botones Anterior/Siguiente */}
       {displayProducts.length > 0 && (
         <div className="pagination">
           <button 
             onClick={() => loadPrevPage({ 
               category: selectedCategory, 
               brands: selectedBrands, 
               searchTerm 
             })}
             disabled={!hasPrevPage}
             className="pagination-btn pagination-prev"
           >
             ← Anterior
           </button>
           
           <div className="pagination-info">
             <span className="page-indicator">Página {currentPage} · Mostrando {displayProducts.length} productos</span>
           </div>

           <button 
             onClick={() => loadNextPage({ 
               category: selectedCategory, 
               brands: selectedBrands, 
               searchTerm 
             })}
             disabled={!hasNextPage}
             className="pagination-btn pagination-next"
           >
             Siguiente →
           </button>
         </div>
       )}
     </div>

      <WhatsAppButton/>

      {/* Agrega el componente de imágenes de ofertas*/}
      <OffersGallery />

       {/* Agrega el componente de imágenes de negocio*/}
       <BusinessGallery />

      <QuantityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmQuantity}
        product={selectedProduct}
      />
      <Footer />
    </div>
  );
};

export default ProductList;

